import { useRef } from 'react';

/**
 * Persists list-page filter state (filters, search, page, size) in sessionStorage
 * so the user lands back on the same filtered view after navigating to a detail page.
 *
 * Usage:
 *   const { loadPersistedState, persistState, clearPersistedState } = usePersistedFilters('company');
 *
 * Keys written: `list_filter:<key>` → JSON blob with { filters, search, page, size }
 */
const PREFIX = 'list_filter:';

export function usePersistedFilters(key) {
  const storageKey = `${PREFIX}${key}`;

  const loadPersistedState = () => {
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };

  const persistState = ({ filters, search, page, size }) => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify({ filters, search, page, size }));
    } catch {
      // sessionStorage full or unavailable — fail silently
    }
  };

  const clearPersistedState = () => {
    try {
      sessionStorage.removeItem(storageKey);
    } catch {}
  };

  return { loadPersistedState, persistState, clearPersistedState };
}
